import React, { useMemo } from 'react';
import { OwcIcon, OwcTypography } from '@one/react';
import { computeGroupInsights } from '../../../utils/general/groupInsights';
import styles from './DashboardInsights.module.css';

/**
 * Insight tile for a single entity type (events / resources / complex data
 * types). Presentational only: takes the derived stats and renders them.
 */
function InsightTile({ icon, count, label, versioned, subGroups }) {
  const meta = [];
  if (subGroups > 0) meta.push(`${subGroups} Sub Group${subGroups === 1 ? '' : 's'}`);

  return (
    <div className={styles.tile}>
      <div className={styles.tileIcon} aria-hidden="true">
        <OwcIcon name={icon} family="outlined" />
      </div>
      <div className={styles.tileBody}>
        <span className={styles.tileCount}>{count}</span>
        <span className={styles.tileLabel}>{label}</span>
        {meta.length > 0 && <span className={styles.tileMeta}>{meta.join(' · ')}</span>}
      </div>
    </div>
  );
}

/**
 * Dashboard empty state shown before a schema is selected. Replaces the plain
 * "Please select a schema to get started" text with an at-a-glance summary of
 * the current data group (entity counts, versioned entities, sub-groups).
 *
 * Data-driven from `groupData`; the visible tiles follow the group's configured
 * dashboard sections, so latest-only groups show only what they have.
 */
function DashboardInsights({ groupData }) {
  const { groupName, subGroupCount, totalEntities, tiles } = useMemo(
    () => computeGroupInsights(groupData),
    [groupData]
  );

  return (
    <div className={styles.wrapper} role="status" aria-label="Data group overview">
      <div className={styles.hero}>
        <div className={styles.heroIcon} aria-hidden="true">
          <OwcIcon name="dashboard" family="outlined" />
        </div>
        <div className={styles.heroText}>
          <OwcTypography className={styles.heroSubtitle}>
            {totalEntities > 0
              ? 'Select an Entity from the sidebar to explore.'
              : 'No entities available.'}
          </OwcTypography>
        </div>
      </div>

      <div className={styles.tiles}>
        {tiles.map((tile) => (
          <InsightTile
            key={tile.id}
            icon={tile.icon}
            count={tile.count}
            label={tile.label}
            versioned={tile.versioned}
          />
        ))}
      </div>
    </div>
  );
}

export default DashboardInsights;
